"use client";

import { useEffect } from "react";

/**
 * Verhalten der apoBank-Landingpages (FAQ-Akkordeon, App-Download-Dialog,
 * Lead-Formular-Embed). Die Logik stammt aus den Inline-Skripten des
 * LP-Exports; sie lebt hier als Client-Komponente, damit sie auch bei
 * Client-seitiger Navigation (next/link) läuft — per innerHTML eingefügte
 * <script>-Tags würden dann nicht ausgeführt. page.tsx entfernt die
 * Inline-Skripte serverseitig aus dem Fragment (keine Doppel-Bindung).
 */
export default function ApoBankLpRuntime({ slug }: { slug: string }) {
  useEffect(() => {
    const cleanups: (() => void)[] = [];

    // FAQ-Akkordeon: Radix-Markup ohne Framework per data-state schalten.
    document
      .querySelectorAll<HTMLButtonElement>("button[data-radix-collection-item]")
      .forEach((btn) => {
        const onClick = () => {
          const open = btn.getAttribute("data-state") === "open";
          const panel = document.getElementById(btn.getAttribute("aria-controls") || "");
          const item = btn.closest("[data-orientation]")?.parentElement ?? null;
          [btn, btn.parentElement, item, panel].forEach((el) => {
            el?.setAttribute("data-state", open ? "closed" : "open");
          });
          btn.setAttribute("aria-expanded", String(!open));
          if (panel) panel.hidden = open;
        };
        btn.addEventListener("click", onClick);
        cleanups.push(() => btn.removeEventListener("click", onClick));
      });

    // App-Download: Store-Auswahl als Dialog, analog zu DownloadCTA.tsx.
    const dialog = document.querySelector<HTMLElement>("[data-apo-appdialog]");
    if (dialog) {
      const setOpen = (open: boolean) => {
        dialog.setAttribute("data-open", String(open));
        dialog.hidden = !open;
        document.body.style.overflow = open ? "hidden" : "";
      };
      const openers = [...document.querySelectorAll<HTMLElement>("[data-apo-app-download]")];
      const onOpen = () => setOpen(true);
      openers.forEach((btn) => {
        btn.addEventListener("click", onOpen);
        cleanups.push(() => btn.removeEventListener("click", onOpen));
      });
      const onDialogClick = (e: MouseEvent) => {
        const t = e.target as HTMLElement;
        if (t === dialog || t.closest("[data-apo-appdialog-close]")) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" && dialog.getAttribute("data-open") === "true") setOpen(false);
      };
      dialog.addEventListener("click", onDialogClick);
      document.addEventListener("keydown", onKey);
      cleanups.push(() => {
        dialog.removeEventListener("click", onDialogClick);
        document.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      });
    }

    // Lead-Formular: embed.js scannt beim Ausführen nach [data-lead-form].
    // Bei jeder (Client-)Navigation neu einhängen, damit der frische
    // Container gefüllt wird; leerer Container = noch kein iframe.
    const holder = document.querySelector("[data-lead-form]");
    if (holder && !holder.querySelector("iframe")) {
      const script = document.createElement("script");
      script.src = "https://form.futuredoctor.group/embed.js";
      script.async = true;
      document.body.appendChild(script);
      cleanups.push(() => script.remove());
    }

    return () => cleanups.forEach((fn) => fn());
  }, [slug]);

  return null;
}
